import { AxiosError } from "axios";
import { serviceInstance } from "utils/axiosInstances";
import { getCognitoClientId } from "config";
import { AuthSession, AuthTokenResponse } from "./session";

/**
 * Thin client for the ServicesServer auth routes
 * (ServicesServer/source/RouteControllers/AuthController.js).
 *
 * The web app uses the Cognito "Web App" client, which has no client secret,
 * so no `clientSecret` is sent.
 */

export async function login(
  email: string,
  password: string
): Promise<AuthTokenResponse> {
  const response = await serviceInstance.post<AuthTokenResponse>("/auth", {
    email,
    password,
    clientId: getCognitoClientId(),
  });
  return response.data;
}

export async function refresh(
  session: AuthSession
): Promise<AuthTokenResponse> {
  const response = await serviceInstance.post<AuthTokenResponse>(
    "/auth/refreshToken",
    {
      refresh_token: session.refreshToken,
      clientId: getCognitoClientId(),
      username: session.email,
      email: session.email,
    }
  );
  return response.data;
}

/** Cognito exception names returned by the server as `{ message }`. */
const FRIENDLY_ERRORS: Record<string, string> = {
  NotAuthorizedException: "Incorrect email or password.",
  UserNotFoundException: "Incorrect email or password.",
  UserNotConfirmedException:
    "Your account hasn't been confirmed yet. Check your email for a confirmation code.",
  PasswordResetRequiredException:
    "You need to reset your password before logging in.",
  TooManyRequestsException: "Too many attempts. Please wait a moment and try again.",
  LimitExceededException: "Too many attempts. Please wait a moment and try again.",
  InvalidParameterException:
    "Login isn't configured correctly for the web app. Please contact support.",
};

export function describeAuthError(error: unknown): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  if (axiosError?.isAxiosError) {
    if (!axiosError.response) {
      return "Couldn't reach the server. Check your connection and try again.";
    }
    const code =
      axiosError.response.data?.message ?? axiosError.response.data?.error;
    if (code && FRIENDLY_ERRORS[code]) return FRIENDLY_ERRORS[code];
    if (axiosError.response.status === 401) return FRIENDLY_ERRORS.NotAuthorizedException;
    return "Something went wrong while logging in. Please try again.";
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong while logging in. Please try again.";
}

/** True when the server definitively rejected the credentials/refresh token. */
export function isRejectedByServer(error: unknown): boolean {
  const status = (error as AxiosError)?.response?.status;
  // 429 is rate limiting, not a verdict on the token - don't log out for it
  return status !== undefined && status >= 400 && status < 500 && status !== 429;
}
